import React from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { INDICATORS_BY_KEY } from '../../../../constants/journey';
import styles from './IndicatorTable.module.css';

// `trend` is the direction the number moved, not a verdict: more steps and a
// higher resting heart rate both arrive as 'up'. Colouring it is the
// frontend's job, and some rows have no better direction at all.
const trendPresentation = (trend, betterDirection) => {
	if (!trend) {
		return { icon: Minus, tone: styles.trendUnknown, label: 'No history yet' };
	}
	if (trend === 'stable') {
		return { icon: Minus, tone: styles.trendNeutral, label: 'Steady' };
	}

	const icon = trend === 'up' ? TrendingUp : TrendingDown;
	const label = trend === 'up' ? 'Higher' : 'Lower';

	if (!betterDirection) {
		return { icon, tone: styles.trendNeutral, label };
	}
	return {
		icon,
		tone: trend === betterDirection ? styles.trendGood : styles.trendPoor,
		label,
	};
};

const IndicatorTable = ({ indicators = [] }) => {
	if (indicators.length === 0) return null;

	// The eleven rows come back on every view, filled or not. Eleven dashes read
	// as a broken table, so say what is actually going on instead.
	const hasAnyData = indicators.some((row) => row.days > 0 && row.average != null);
	if (!hasAnyData) {
		return (
			<p className={styles.noData}>
				<span>No wearable data was recorded in this period.</span>
				<span>
					Steps, sleep and heart rate appear here once a device has synced.
				</span>
			</p>
		);
	}

	return (
		<div className={styles.wrapper}>
			<ul className={styles.list}>
				{indicators.map((row) => {
					const meta = INDICATORS_BY_KEY[row.key];
					if (!meta) return null;

					const Icon = meta.icon;
					const hasValue = row.average != null && row.days > 0;
					const { icon: TrendIcon, tone, label } = trendPresentation(
						row.trend,
						meta.betterDirection
					);

					return (
						<li key={row.key} className={styles.row}>
							<span className={styles.iconCell}>
								<Icon className={styles.icon} />
							</span>

							<span className={styles.label}>{meta.label}</span>

							<span className={styles.days}>
								{row.days === 0
									? 'No days with data'
									: `${row.days} ${row.days === 1 ? 'day' : 'days'} of data`}
							</span>

							<span className={styles.valueCell}>
								{hasValue ? (
									<>
										<span className={styles.value}>
											{meta.format(row.average)}
										</span>
										<span className={styles.unit}>{meta.unit}</span>
									</>
								) : (
									<span className={styles.noValue}>—</span>
								)}
							</span>

							<span className={`${styles.trendCell} ${tone}`} title={label}>
								<TrendIcon className={styles.trendIcon} />
								<span className={styles.trendLabel}>{label}</span>
							</span>
						</li>
					);
				})}
			</ul>

			<p className={styles.footnote}>
				Trends compare this period&apos;s average against the 21 days before it.
				Sleep stages are not available from the wearable data we receive.
			</p>
		</div>
	);
};

export default IndicatorTable;
